/**
 * Database Helpers
 *
 * Internal helper functions for database operations.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { logger } from '@/utils/logger';
import type { Project } from './types';

// ============================================
// COMMON ERROR HANDLING
// ============================================

/**
 * Handle Supabase PGRST116 (not found) errors by returning a default value
 * instead of throwing. Re-throws all other errors.
 */
export function handleNotFoundError<T>(
  error: { code?: string; message: string },
  defaultValue: T
): T {
  if (error.code === 'PGRST116') return defaultValue;
  throw error;
}

/**
 * Map Postgres/Supabase error codes to user-friendly messages.
 */
export function formatDatabaseError(error: { code?: string; message?: string }): string {
  const code = error.code;
  const message = error.message || '';

  // Postgres error codes
  switch (code) {
    case '42501':
      return 'You do not have permission to perform this action';
    case '23505':
      return 'An item with this name already exists';
    case '23503':
      return 'Cannot complete this action because it references data that no longer exists';
    case '23502':
      return 'A required field is missing';
    case '23514':
      return 'The provided value is not valid';
    case '42P01':
      return 'Internal error: resource not found';
    case 'PGRST116':
      return 'The requested item was not found';
  }

  // Supabase-specific patterns
  if (message.includes('already exists')) {
    return 'An item with this name already exists';
  }
  if (message.includes('violates row-level security')) {
    return 'You do not have permission to perform this action';
  }

  return message || 'An unexpected error occurred';
}

// ============================================
// UPDATE PAYLOAD BUILDER
// ============================================

/**
 * Build a Supabase update payload from a camelCase updates object
 * using a field mapping of camelCase → snake_case.
 *
 * Only includes fields that are present (not undefined) in `updates`.
 */
export function buildUpdatePayload(
  updates: Record<string, unknown>,
  fieldMapping: Record<string, string>
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const [camelKey, snakeKey] of Object.entries(fieldMapping)) {
    if (updates[camelKey] !== undefined) {
      payload[snakeKey] = updates[camelKey];
    }
  }
  return payload;
}

// Supabase row type for spaces table
interface SupabaseSpaceRow {
  id: string;
  created_at: string;
  type: 'project' | 'folder' | 'list' | 'document' | 'chat' | 'voice_channel' | 'spreadsheet' | 'presentation' | 'category';
  parent_id: string | null;
  project_id: string | null;
  name: string;
  description: string | null;
  content: string | unknown[] | Record<string, unknown> | null;
  order_index: number | null;
  updated_at: string;
}

// Helper to convert Supabase space to Project format
export function convertSpaceFromSupabase(row: SupabaseSpaceRow): Project {
  return row as Project;
}

// Helper function to get root project ID from any container in the hierarchy
export async function getRootProjectId(containerId: string | null): Promise<string | null> {
  if (!containerId) return null;

  let currentId: string | null = containerId;
  const visited = new Set<string>();

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);

    const {
      data: container,
      error,
    }: { data: { id: string; type: string; parent_id: string | null; project_id: string | null } | null; error: { message: string; code?: string } | null } =
      await withRetry(() =>
        supabase.from('spaces').select('id, type, parent_id, project_id').eq('id', currentId!).single()
      );

    if (error || !container) return null;

    if (container.type === 'project') {
      return container.id;
    }

    // If container has project_id, use it (already points to root)
    if (container.project_id) {
      return container.project_id;
    }

    // Otherwise, traverse up using parent_id
    currentId = container.parent_id;
  }

  return null;
}

// Helper function to get next order index (atomic via RPC)
export async function getNextOrderIndex(parentId: string | null, type: string): Promise<number> {
  // Try RPC first
  try {
    const { data, error } = await withRetry(() =>
      supabase.rpc('get_next_order_index', {
        p_type: type,
        p_parent_id: parentId,
      })
    );
    if (!error) return (data ?? 0) as number;
  } catch (err) { logger.warn('get_next_order_index RPC failed, using fallback:', err); }

  // Fallback: query directly
  let query = supabase
    .from('spaces')
    .select('order_index')
    .eq('type', type)
    .order('order_index', { ascending: false })
    .limit(1);

  if (parentId === null) {
    query = query.is('parent_id', null);
  } else {
    query = query.eq('parent_id', parentId);
  }

  const { data, error } = await withRetry(() => query);
  if (error) throw error;
  return data && data.length > 0 ? (data[0].order_index ?? 0) + 1 : 0;
}
